"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Animated count-up that triggers when scrolled into view.
 * Accepts a numeric target; renders formatted string.
 */
export function Counter({
  to,
  duration = 1800,
  decimals = 0,
  prefix = "",
  suffix = "",
  className,
}: {
  to: number;
  duration?: number;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const [val, setVal] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const start = () => {
      if (started.current) return;
      started.current = true;
      const t0 = performance.now();
      const tick = (now: number) => {
        const p = Math.min((now - t0) / duration, 1);
        // easeOutExpo
        const eased = p === 1 ? 1 : 1 - Math.pow(2, -10 * p);
        setVal(to * eased);
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };
    // Start the animation on mount with a tiny delay. This way the counter
    // always animates to its final value during the first second after
    // hydration, regardless of viewport position. Users who scroll to the
    // counter see either the final value (most common) or the tail end of
    // the animation. Without auto-start, IntersectionObserver misses
    // below-the-fold elements that are revealed by parent Reveal wrappers,
    // leaving the counter stuck at 0.
    const kick = window.setTimeout(start, 60);
    return () => {
      window.clearTimeout(kick);
    };
  }, [to, duration]);

  return (
    <span ref={ref} className={className}>
      {prefix}
      {val.toLocaleString("en-US", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}
      {suffix}
    </span>
  );
}
